import { Box, Heading, Paragraph } from 'grommet'
import Image from 'next/image'
import styled, { css } from 'styled-components'

import { NormalizedWrapper } from '@theme'

type SectionItemProps = {
  color: string
}

export const SectionWrapper = styled.div`
  ${({ theme }) => css`
    display: grid;
    font-weight: ${theme.typography.mobile.body.body2.fontWeight};
    grid-auto-rows: 1fr;
    grid-row-gap: ${theme.newSpaces.xs2}px;
    position: relative;
    width: 100%;
  `}
`

export const SectionItem = styled.div<SectionItemProps>`
  background-color: ${({ color }) => color};
  ${({ theme }) => css`
    padding: ${theme.newSpaces.md}px ${theme.newSpaces.sm}px;
    width: 100%;

    ${theme.below.mobileXL`
      font-size: ${theme.typography.mobile.body.body3.fontSize}px;
    `}
  `}
`

export const TextSections = styled.p`
  white-space: pre-wrap;
  ${({ theme }) => css`
    color: ${theme.newColors.darkGray};

    ${theme.getTypography('desktop', 'body', 'body2')}

    ${theme.below.mobileXL`
      ${theme.getTypography('mobile', 'body', 'body4')}
    `}
  `}
`

export const HexagonLeft = styled(Image)`
  ${({ theme }) => css`
    left: 0%;
    position: absolute;
    top: 6%;

    ${theme.below.tabletS`
      width: auto;
    `}
  `}
`

export const HexagonRight = styled(Image)`
  ${({ theme }) => css`
    height: 210px;
    position: absolute;
    right: 0%;
    width: 210px;

    ${theme.below.tabletS`
      width: auto;
      top: 44%;
    `}
  `}
`

export const Wrapper = styled(NormalizedWrapper)`
  ${({ theme }) => css`
    ${theme.below.laptop`
      padding: 0 ${theme.newSpaces.sm}px;
    `}

    ${theme.below.mobileXL`
      padding: 0 ${theme.newSpaces.lg}px;
      margin-bottom: -${theme.newSpaces.lg}px;
    `}
  `}
`

export const Cards = styled(Box)`
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;

  ${({ theme }) => css`
    margin-top: ${theme.newSpaces.xl10}px;

    ${theme.below.tablet`
      justify-content: center;
      margin-top: ${theme.newSpaces.xl2}px;
    `}
  `}
`

export const Card = styled(Box)`
  width: 31%;

  ${({ theme }) => css`
    align-items: center;
    margin-bottom: ${theme.newSpaces.lg}px;

    ${theme.below.tablet`
      width: 50%;

      &:nth-last-child(n + 2) {
        margin-bottom: ${theme.newSpaces.md}px;
      }
    `}

    ${theme.below.tabletS`
      width: 100%;
    `}
  `}
`

export const TableTitle = styled(Heading)`
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-align: center;
  text-transform: uppercase;
  width: fit-content;

  span {
    font-weight: 600;
  }

  ${({ theme }) => css`
    ${theme.getTypography('desktop', 'title', 'large')}

    background-image: ${theme.newColors.gradientGreen};

    ${theme.below.tablet`
      margin: 0 auto;
    `}
  `}
`

export const TableSubtitle = styled(Paragraph)`
  text-align: center;
  text-transform: uppercase;
  white-space: pre-wrap;
  width: 100%;

  ${({ theme }) => css`
    ${theme.getTypography('mobile', 'title', 'blog')};

    color: ${theme.newColors.primary};
    margin: ${theme.newSpaces.md}px auto 0;
  `}
`

export const ResultImage = styled(Image)`
  ${({ theme }) => css`
    height: 100%;
    margin-bottom: ${theme.newSpaces.md}px;
    width: 100%;

    ${theme.below.mobileL`
      margin-bottom: ${theme.newSpaces.xs}px;
    `};
  `}
`

export const List = styled.ul`
  ${({ theme }) => css`
    list-style-image: url('/images/list-hexagon-dark.webp');
    margin-bottom: ${theme.newSpaces.xl2}px;
    margin-top: ${theme.newSpaces.xs}px;
    padding-left: ${theme.newSpaces.sm}px;

    ${theme.below.tabletS`
      margin-bottom: ${theme.newSpaces.xs}px;
      list-style-image: none;
    `}
  `}
`

export const ListItem = styled.li`
  ${({ theme }) => css`
    span {
      font-weight: ${theme.typography.desktop.title.title4.fontWeight};
      text-transform: uppercase;
    }

    ${theme.getTypography('desktop', 'body', 'body2')}

    color: ${theme.newColors.darkGray};
    margin: ${theme.newSpaces.sm}px 0;
    padding-left: ${theme.newSpaces.xs2}px;

    ${theme.below.mobileL`
      ${theme.getTypography('mobile', 'body', 'body3')};

      font-weight: ${theme.typography.mobile.body.body2.fontWeight};
      margin-top: 0;
      padding-left: 0;
      margin-bottom: ${theme.newSpaces.xs}px;
    `}
  `}
`

export const Title = styled(Heading)`
  ${({ theme }) => css`
    ${theme.getTypography('desktop', 'title', 'title1')}

    color: ${theme.newColors.primary};
    margin-bottom: ${theme.newSpaces.xs}px;
    width: 100%;

    ${theme.below.mobileXL`
      ${theme.getTypography('mobile', 'title', 'title2')}

      font-size: ${theme.typography.mobile.title.title1.fontSize}px;
    `}
  `}
`

export const SubTitle = styled(Paragraph)`
  ${({ theme }) => css`
    ${theme.getTypography('desktop', 'body', 'body2')}

    color: ${theme.newColors.darkGray};
    margin: ${theme.newSpaces.xs}px auto ${theme.newSpaces.xs}px auto;
    max-width: 100%;
    text-align: justify;
    white-space: pre-wrap;

    a {
      ${theme.getTypography('desktop', 'body', 'body2')}
    }

    ${theme.below.mobileXL`
      ${theme.getTypography('mobile', 'body', 'body3')}

      font-weight: ${theme.typography.mobile.body.body2.fontWeight};
      text-align: start;

      a {
        ${theme.getTypography('mobile', 'body', 'body3')}

        font-weight: ${theme.typography.mobile.body.body2.fontWeight};
      }
    `};
  `}
`
